import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchMemoDetails } from "../../../../shared/hooks/useFetchPayment";
import API_URLS from "../../Utils/axr";
import { errorConditionGTM } from "../../Utils/googleTags";
import store from "../store";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import DwLogutil from "../../Utils/DwLogutil";

type UpcomingBill = {
  label: string;
  monthlyCharge: number;
  advanceApplied: number;
  freeNextMonthInternet: number;
  totalDue: number;
  carriesToNext: boolean;
  remainingAdvance: number;
};

type CreditMemoInitialStateType = {
  creditMemo: any;
  advancePayment: number;
  creditMemoloading: boolean;
  error: any;
  upcomingBills: UpcomingBill[];
};

const initialState: CreditMemoInitialStateType = {
  creditMemo: null,
  advancePayment: 0,
  creditMemoloading: false,
  error: null,
  upcomingBills: [],
};

const ORDINAL_LABELS: Record<number, string> = {
  2: "Second bill",
  3: "Third bill",
};

export function getUpcomingMonthlyBills(
  recurringMonthlyCharge: number,
  leftoverAdvancePayment: number,
  freeNextMonthInternet: number
): UpcomingBill[] {
  const monthlyCharge = Math.max(
    parseAmount(recurringMonthlyCharge),
    0
  );
  const freeInternetAmount = Math.max(parseAmount(freeNextMonthInternet),0);

  let remainingAdvance = Math.max(
    parseAmount(leftoverAdvancePayment),
    0
  );

  const schedule: UpcomingBill[] = [];

  // Business requirement: always show the next 2 bills.
  for (let monthIndex = 2; monthIndex <= 3; monthIndex++) {
    const advanceApplied = Math.min(
      remainingAdvance,
      monthlyCharge
    );

    const totalDue = Number(
      (monthlyCharge - advanceApplied - freeInternetAmount).toFixed(2)
    );
    remainingAdvance = Number(
      Math.max(remainingAdvance - advanceApplied, 0).toFixed(2)
    );

    schedule.push({
      label: ORDINAL_LABELS[monthIndex],
      monthlyCharge: Number(monthlyCharge.toFixed(2)),
      advanceApplied: Number(advanceApplied.toFixed(2)),
      freeNextMonthInternet: Number(freeInternetAmount.toFixed(2)),
      totalDue,
      carriesToNext: remainingAdvance > 0,
      remainingAdvance,
    });
  }

  return schedule;
}

function parseAmount(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export const getCreditMemo = createAsyncThunk(
  "credit/getCreditMemo",
  async (billingAccountNumber: string) => {
    try {
      const reduxStore = store.getState() as any;
      const headers = reduxStore?.brightspeedToken?.headers;
      const serviceAddrId = reduxStore?.addressQualification?.data?.addressQualification?.entity?.serviceAddrId;
      const transactionId = reduxStore?.addressQualification?.data?.addressQualification?.entity?.partnerSessionId;
      const isMigrated = reduxStore?.user?.data?.migratedAccount && reduxStore?.user?.data?.migratedAccount?.toUpperCase() === "Y" ? "Y" : "N";
      const payload = {
        baseline: {
          transactionId: transactionId,
          partnerId: "BS196123",
          salesCode: "20000660",
          partnerReferenceId: "",
          agentId: "",
          channel: "web-acm",
          siteId: headers?.siteId?.toLowerCase(),
        },
        planDetailRequest: {
          accountNumber: billingAccountNumber,
          addressId: serviceAddrId,
          flow: "change",
          isMigrated: isMigrated,
        },
      };
      const url = API_URLS["getCreditMemoUrl"];      
      const params = {
        "": [url],
      };
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.one_time_credit_API_attempted }); 
      const response = await fetchMemoDetails(params, headers, payload);       
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.one_time_credit_API_success });   
      return response?.data;
    } catch (error) {
      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.one_time_credit_API_failed,
        error: (error as any)?.response?.data?.message || (error as any)?.message || "Credit memo API failed",
      });
      throw error;
    }
  }
);

const getCreditMemoSlice = createSlice({
  name: "credit/getCreditMemo",
  initialState,
  reducers: {
    clearCreditMemoState: () => initialState,
    creditMemoSetInitialState: () => initialState,

    setUpcomingBills: (
      state,
      action: PayloadAction<{
        recurringMonthlyCharge: number;
        leftoverAdvancePayment: number;
        freeNextMonthInternet: number;
      }>
    ) => {
      const { recurringMonthlyCharge, leftoverAdvancePayment, freeNextMonthInternet, } = action.payload;
      state.upcomingBills = getUpcomingMonthlyBills(
        recurringMonthlyCharge,
        leftoverAdvancePayment,
        freeNextMonthInternet
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCreditMemo.pending, (state) => {
      state.creditMemoloading = true;
      state.error = null;
    });

    builder.addCase(getCreditMemo.fulfilled, (state, action) => {
      state.creditMemo = action.payload;
      state.advancePayment = Number(parseAmount(action.payload?.advancePayment).toFixed(2));
      state.creditMemoloading = false;
      state.error = null;
    });

    builder.addCase(getCreditMemo.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "Credit memo",
        action.error?.message,
        action?.type
      );
      state.creditMemo = null;
      state.advancePayment = 0;
      state.upcomingBills = [];
      state.creditMemoloading = false;
      state.error = action.error;
    });
  },
});

export const { clearCreditMemoState, creditMemoSetInitialState, setUpcomingBills } = getCreditMemoSlice.actions;

export default getCreditMemoSlice.reducer;